import { useNavigate, useParams } from "react-router-dom";
import ListaVazia from "../../../../utils/listaVazia";
import ObservacoesFeitas from "../observacoesFeitas";
import StatusEstabelecimento from "../../../../utils/status/statusEstabelecimento";
import { useSelector } from "react-redux";
import ImagemEdit from "../../../../utils/imagemEdit";
import StatusEntrada from "../../../../utils/status/statusEntrada";
import { temPermissao } from "../../../../utils/permissoes";
import Relatorios from "../../../../relatorioEspontaneo/relatoriosEspontaneos";
import { useEffect, useMemo, useState } from "react";
import api from "../../../../services/api";

// ── Botão de acção do workflow ────────────────────────────────
function ActionBtn({ onClick, variant = 'success', active = false, disabled = false, children }) {
  const base = 'tw-w-full tw-text-sm tw-font-semibold tw-rounded-lg tw-px-4 tw-py-2 tw-transition-all tw-duration-200 tw-border';
  const colors = {
    success: active
      ? 'tw-bg-green-600 tw-text-white tw-border-green-600 hover:tw-bg-green-700'
      : 'tw-border-green-600 tw-text-green-700 hover:tw-bg-green-50',
    error: active
      ? 'tw-bg-red-500 tw-text-white tw-border-red-500 hover:tw-bg-red-600'
      : 'tw-border-red-400 tw-text-red-500 hover:tw-bg-red-50',
  };
  const disabledCls = disabled ? 'tw-opacity-40 tw-cursor-not-allowed' : 'tw-cursor-pointer';

  return (
    <button
      onClick={!disabled ? onClick : undefined}
      className={`${base} ${colors[variant]} ${disabledCls}`}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
export default function NavLateral({ estabelecimento, observacoesFeitas }) {
  const user = useSelector(state => state.account.user);
  const { id, encaminhar } = useParams();
  const navigate = useNavigate();
  const [inspeccoes, setInspeccoes] = useState([]);


  // Buscar inspeções do estabelecimento, para determinar se pode encaminhar para inspeção
  useEffect(() => {
    const fetchInspeccoes = async () => {
      try {
        const response = await api.get(`/inspeccaos?estabelecimentoId=${estabelecimento?._id}`);
        setInspeccoes(response.data.inspeccaos || []);
      } catch (error) {
        setInspeccoes([]);
        console.error('Erro ao buscar o estabelecimento:', error);
      }
    };

    fetchInspeccoes();

  }, [estabelecimento?._id]);

  const podeEncaminharParaInspeccao = useMemo(() => {
    if (inspeccoes.length === 0) return true;
    return inspeccoes.every((inspeccao) => inspeccao?.status === 'Reprovada');
  }, [inspeccoes]);

  // console.log(estabelecimento)

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">

      {/* ── Cartão de informação ── */}
      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">

        {/* Foto */}
        <div className="tw-flex tw-justify-center tw-mb-3">
          <ImagemEdit
            data={estabelecimento}
            url="/estabelecimento/change-logo"
            local="estabelecimentoId"
            openEditLogo={false}
          />
        </div>

        {/* Tipo de entrada */}
        <p className="tw-text-[11px] tw-text-gray-500 tw-text-center tw-uppercase tw-tracking-wide tw-mb-1 tw-font-semibold">
          Tipo de entrada
        </p>
        <StatusEntrada status={estabelecimento.tipoEntrada} origem={estabelecimento?.external?.nome} />

        {/* Estado */}
        <p className="tw-text-[11px] tw-text-gray-500 tw-text-center tw-uppercase tw-tracking-wide tw-mt-3 tw-mb-1 tw-font-semibold">
          Estado da solicitação
        </p>
        <StatusEstabelecimento status={estabelecimento.status} />

        {/* Divisor */}
        <div className="tw-h-px tw-bg-gray-100 tw-my-4" />

        {/* Botões de acção do workflow */}
        {estabelecimento?.status !== 'Activo' ? (
          <div className="tw-flex tw-flex-col tw-gap-2">

            {estabelecimento?.status === 'Reprovado' && (
              <ActionBtn active={encaminhar === 'approve'} onClick={() => navigate(`/admin/approve/Análise/${id}`)}>
                Encaminhar para Análise
              </ActionBtn>
            )}

            {estabelecimento?.status === 'Análise' && (
              <>
                <ActionBtn
                  disabled={!temPermissao(user, ['licenciamentoEstabelecimentoTecnico'])}
                  active={encaminhar === 'approve'}
                  onClick={() => navigate(`/admin/approve/Analisado/${id}`)}
                >
                  Encaminhar para Chefe de Área
                </ActionBtn>
                <ActionBtn
                  disabled={!temPermissao(user, ['licenciamentoEstabelecimentoTecnico'])}
                  variant="error"
                  active={encaminhar === 'repprove'}
                  onClick={() => navigate(`/admin/repprove/Reprovado/${id}`)}
                >
                  Reprovar Solicitação
                </ActionBtn>
              </>
            )}

            {estabelecimento?.status === 'Analisado' && (
              <>
                <ActionBtn
                  disabled={!temPermissao(user, ['licenciamentoEstabelecimentoChefeSeccao'])}
                  active={encaminhar === 'approve'}
                  onClick={() => navigate(`/admin/approve/Analisado Chefe de Secção/${id}`)}
                >
                  Encaminhar para Chefe de Departamento
                </ActionBtn>
                <ActionBtn
                  disabled={!temPermissao(user, ['licenciamentoEstabelecimentoChefeSeccao'])}
                  variant="error"
                  active={encaminhar === 'repprove'}
                  onClick={() => navigate(`/admin/repprove/Análise/${id}`)}
                >
                  Voltar para Análise
                </ActionBtn>
              </>
            )}

            {estabelecimento?.status === 'Analisado Chefe de Secção' && (
              <>
                <ActionBtn
                  disabled={
                    !temPermissao(user, ['licenciamentoEstabelecimentoChefeDepartamento']) ||
                    !podeEncaminharParaInspeccao
                  }
                  active={encaminhar === 'approve'}
                  onClick={() => navigate(`/admin/inspecao/approve/${id}`)}
                >
                  Encaminhar para Inspecção
                </ActionBtn>
                {!podeEncaminharParaInspeccao && (
                  <p className="tw-text-xs tw-leading-5 tw-text-amber-700">
                    Só é possível encaminhar para inspecção quando for a primeira inspecção do estabelecimento ou quando todas as inspecções anteriores estiverem com Reprovadas.
                  </p>
                )}
                <ActionBtn
                  disabled={!temPermissao(user, ['licenciamentoEstabelecimentoChefeDepartamento'])}
                  variant="error"
                  active={encaminhar === 'repprove'}
                  onClick={() => navigate(`/admin/repprove/Analisado/${id}`)}
                >
                  Voltar para Chefe de Área
                </ActionBtn>
              </>
            )}

            {estabelecimento?.status === 'Inspecção Agendada' && (
              <>
                <ActionBtn
                  disabled={!temPermissao(user, ['licenciamentoEstabelecimentoInspeccaoAgendada'])}
                  active
                  onClick={() => navigate(`/admin/inspeccionar-estabelecimento/${id}`)}
                >
                  Inspeccionar Agora
                </ActionBtn>
                <ActionBtn variant="error" disabled>
                  Cancelar Inspecção
                </ActionBtn>
              </>
            )}

            {estabelecimento?.status === 'Inspecção Realizada' && (
              <>
                <ActionBtn
                  disabled={!temPermissao(user, ['licenciamentoEstabelecimentoInspeccaoRealizada'])}
                  active={encaminhar === 'approve'}
                  onClick={() => navigate(`/admin/approve/Directora Geral Adjunta para os Serviços Executivos/${id}`)}
                >
                  Encaminhar para Director(a) Geral Adjunto(a) para SE
                </ActionBtn>
                {estabelecimento?.statusInspeccao === 'Reprovada' && (
                  <ActionBtn
                    disabled={!temPermissao(user, ['licenciamentoEstabelecimentoInspeccaoRealizada'])}
                    variant="error"
                    active={encaminhar === 'repprove'}
                    onClick={() => navigate(`/admin/inspecao/approve/${id}`)}
                  >
                    Reagendar Inspecção
                  </ActionBtn>
                )}
              </>
            )}

            {estabelecimento?.status === 'Directora Geral Adjunta para os Serviços Executivos' && (
              <>
                <ActionBtn
                  disabled={!temPermissao(user, ['licenciamentoEstabelecimentoDirectorGeralAdjunta'])}
                  active={encaminhar === 'approve'}
                  onClick={() => navigate(`/admin/approve/Deferimento/${id}`)}
                >
                  Encaminhar para Director(a) Geral
                </ActionBtn>
                <ActionBtn
                  disabled={!temPermissao(user, ['licenciamentoEstabelecimentoDirectorGeralAdjunta'])}
                  variant="error"
                  active={encaminhar === 'repprove'}
                  onClick={() => navigate(`/admin/repprove/Inspecção Realizada/${id}`)}
                >
                  Voltar para Chefe de Departamento
                </ActionBtn>
              </>
            )}

            {estabelecimento?.status === 'Deferimento' && (
              <>
                <ActionBtn
                  disabled={!temPermissao(user, ['licenciamentoEstabelecimentoDeferimento'])}
                  active={encaminhar === 'approve'}
                  onClick={() => navigate(`/admin/emissao/autorizacao/${id}`)}
                >
                  Aprovar e Emitir Autorização de Exercício
                </ActionBtn>
                <ActionBtn
                  disabled={!temPermissao(user, ['licenciamentoEstabelecimentoDeferimento'])}
                  variant="error"
                  active={encaminhar === 'repprove'}
                  onClick={() => navigate(`/admin/repprove/Inspecção Realizada/${id}`)}
                >
                  Voltar para Chefe de Departamento
                </ActionBtn>
              </>
            )}

          </div>
        ) : (
          <ListaVazia text={'Processo já analisado!'} />
        )}
      </div>

      {/* ── Relatórios Espontâneos ── */}
      <Relatorios processoId={estabelecimento?._id} modelName="estabelecimentos" />

      {/* ── Observações ── */}
      {observacoesFeitas?.length > 0 && (
        <ObservacoesFeitas observacoesFeitas={observacoesFeitas} />
      )}
    </div>
  );
}
